
import React, { useState } from 'react';
import { useSermons } from '../../hooks/useSermons';
import { parseISO } from 'date-fns';
import { 
  Languages, 
  Loader2, 
  AlertCircle, 
  Download,
  Save,
  FileText,
  Globe,
  ChevronDown,
  Check,
  RefreshCw,
  X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { jsPDF } from 'jspdf';
import { createStyledPDF, addSection, cleanAIText } from '../../lib/pdf';

interface TranslatedSermon {
  id: string;
  originalId: string;
  title: string;
  content: string;
  language: string;
  timestamp: number;
  originalSermon?: any;
  translatedScripture?: string;
  sections?: Array<{
    type: string;
    content: string;
    pointNumber?: string;
    attribution?: string;
    isSlide?: boolean;
    customTitle?: string;
  }>;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const SUPPORTED_LANGUAGES: Language[] = [
  // Latin script languages - well supported
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  // Cyrillic script - generally well supported
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  // CJK languages - well supported with proper fonts
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' }
];

export function SermonTranslator({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translatedSermons, setTranslatedSermons] = useState<TranslatedSermon[]>([]);
  const [currentTranslation, setCurrentTranslation] = useState<TranslatedSermon | null>(null);
  
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Load saved translations on mount
  React.useEffect(() => {
    const savedTranslations = localStorage.getItem('sermon_translations');
    if (savedTranslations) {
      try {
        const parsed = JSON.parse(savedTranslations);
        setTranslatedSermons(parsed);
      } catch (error) {
        console.error('Error loading saved translations:', error);
      }
    }
  }, []);

  // Save translations to localStorage whenever they change
  React.useEffect(() => {
    if (translatedSermons.length > 0) {
      localStorage.setItem('sermon_translations', JSON.stringify(translatedSermons));
    }
  }, [translatedSermons]);

  const filteredLanguages = SUPPORTED_LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(languageSearchTerm.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(languageSearchTerm.toLowerCase())
  );

  const translateSermon = async () => {
    const selectedSermon = sermons.find(s => s.id === selectedSermonId);
    if (!selectedSermon || !selectedLanguage) return;

    try {
      setTranslating(true);
      setTranslationError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');

      // Parse the sermon content to extract sections
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = selectedSermon.content;
      
      // Single unified traversal preserving DOM order (sections and prose interleaved)
      const contentItems: Array<{
        kind: 'section' | 'prose';
        type: string;
        content: string;
        pointNumber?: string;
        attribution?: string;
        isSlide?: boolean;
        customTitle?: string;
      }> = [];

      // Keep sections array for fallback code
      const sections: Array<{
        type: string;
        content: string;
        pointNumber?: string;
        attribution?: string;
        isSlide?: boolean;
        customTitle?: string;
      }> = [];

      tempDiv.childNodes.forEach(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const element = node as Element;

        if (element.classList.contains('sermon-section')) {
          const type = element.getAttribute('data-type') || 'custom';
          const pointNumber = element.getAttribute('data-point-number') || undefined;
          const attribution = element.getAttribute('data-attribution') || undefined;
          const isSlide = element.getAttribute('data-is-slide') === 'true';
          const customTitle = element.getAttribute('data-custom-title') || undefined;
          const sectionContent = element.innerHTML || element.textContent || '';

          const item = {
            kind: 'section' as const,
            type,
            content: sectionContent.trim(),
            pointNumber,
            attribution,
            isSlide,
            customTitle
          };
          contentItems.push(item);
          sections.push(item);
        } else {
          const html = element.innerHTML || element.textContent || '';
          if (html.trim()) {
            contentItems.push({ kind: 'prose', type: 'prose', content: html.trim() });
          }
        }
      });

      // Prepare content for translation — all items in DOM order with sequential markers
      let contentToTranslate = `Title: ${selectedSermon.title}\nScripture: ${selectedSermon.scripture || 'Multiple Passages'}\n\n`;

      contentItems.forEach((item, index) => {
        contentToTranslate += `[SECTION_START:${index}:${item.type}]\n`;
        if (item.kind === 'section') {
          if (item.pointNumber) contentToTranslate += `[POINT_NUMBER:${item.pointNumber}]\n`;
          if (item.attribution) contentToTranslate += `[ATTRIBUTION:${item.attribution}]\n`;
          if (item.customTitle) contentToTranslate += `[CUSTOM_TITLE:${item.customTitle}]\n`;
        }
        contentToTranslate += item.content + '\n';
        contentToTranslate += `[SECTION_END:${index}]\n\n`;
      });
      
      contentToTranslate = contentToTranslate.trim();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          type: 'translate',
          content: contentToTranslate,
          targetLanguage: selectedLanguage.code,
          languageName: selectedLanguage.name
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Translation API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        console.error('Translation API returned error:', data);
        throw new Error(data.error || 'Translation service returned an error');
      }

      // Parse the translated content
      const translatedContent = data.content;
      
      // DEBUG: Log the raw translated content to see what the AI returned
      console.log('🔍 RAW TRANSLATED CONTENT (first 500 chars):', translatedContent.substring(0, 500));
      
      // Extract translated title from the response
      const titleMatch = translatedContent.match(/Title:\s*(.*?)(?:\n|$)/i);
      const translatedTitle = titleMatch ? titleMatch[1].trim() : `${selectedSermon.title} (${selectedLanguage.name})`;
      
      // Extract translated scripture reference
      const scriptureMatch = translatedContent.match(/Scripture:\s*(.*?)(?:\n|$)/i);
      const translatedScripture = scriptureMatch ? scriptureMatch[1].trim() : (selectedSermon.scripture || 'Multiple Passages');
      
      // Parse the translated section content - try with section markers first
      const translatedSections: typeof sections = [];
      // Updated regex to capture type and optional translated title: [SECTION_START:index:type|translatedTitle] or [SECTION_START:index:type]
      const sectionRegex = /\[SECTION_START:(\d+):([^|\]]+)(?:\|([^\]]+))?\]([\s\S]*?)\[SECTION_END:\1\]/g;
      let match;
      
      while ((match = sectionRegex.exec(translatedContent)) !== null) {
        const sectionIndex = parseInt(match[1]);
        const sectionType = match[2].trim();
        const translatedTitle = match[3]?.trim();
        let sectionText = match[4].trim();

        // Remove metadata markers from the translated content
        sectionText = sectionText
          .replace(/\[POINT_NUMBER:.*?\]\n?/g, '')
          .replace(/\[ATTRIBUTION:.*?\]\n?/g, '')
          .replace(/\[CUSTOM_TITLE:.*?\]\n?/g, '')
          .trim();

        if (sectionType === 'prose') {
          // Non-section prose content — no sermon-section metadata needed
          translatedSections.push({ type: 'prose', content: sectionText });
        } else if (contentItems[sectionIndex]) {
          // Regular sermon section — copy metadata from original
          translatedSections.push({
            ...contentItems[sectionIndex],
            content: sectionText,
            customTitle: translatedTitle || contentItems[sectionIndex].customTitle
          });
        }
      }
      
      // DEBUG: Log results
      console.log(`📊 Total sections found: ${translatedSections.length}`);
      if (translatedSections.length === 0) {
        console.warn('⚠️ No sections found with markers - will use fallback');
      }

      // IMPROVED FALLBACK: If section markers weren't preserved, try to extract translated content manually
      if (translatedSections.length === 0 && sections.length > 0) {
        console.warn('⚠️ Section markers not found in translation. Using fallback content extraction - structure may not be perfectly preserved.');
        
        // Remove the title and scripture lines from translated content
        let mainContent = translatedContent
          .replace(/Title:\s*.*?(?:\n|$)/i, '')
          .replace(/Scripture:\s*.*?(?:\n|$)/i, '')
          .trim();
        
        // Split content into paragraphs
        const paragraphs = mainContent.split(/\n\n+/).filter(p => {
          const trimmed = p.trim();
          return trimmed.length > 0 && 
                 !trimmed.match(/\[SECTION_|Title:|Scripture:/);
        });
        
        // Use character-based distribution instead of paragraph count to better preserve proportions
        const totalChars = mainContent.length;
        let charIndex = 0;
        
        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];
          const sectionCharRatio = section.content.length / sections.reduce((sum, s) => sum + s.content.length, 0);
          const targetChars = Math.floor(totalChars * sectionCharRatio);
          
          // Collect paragraphs until we reach the target character count
          let sectionContent = '';
          let sectionParagraphs = [];
          
          while (charIndex < paragraphs.length && sectionContent.length < targetChars) {
            sectionParagraphs.push(paragraphs[charIndex]);
            sectionContent = sectionParagraphs.join('\n\n');
            charIndex++;
          }
          
          // Ensure at least one paragraph per section if available
          if (sectionParagraphs.length === 0 && charIndex < paragraphs.length) {
            sectionParagraphs.push(paragraphs[charIndex]);
            charIndex++;
          }
          
          if (sectionParagraphs.length > 0) {
            translatedSections.push({
              ...section, // Preserves type, pointNumber, attribution, and all metadata
              content: sectionParagraphs.join('\n\n').trim()
            });
          }
        }
        
        // Append any remaining paragraphs to the last section
        if (charIndex < paragraphs.length && translatedSections.length > 0) {
          const remainingParagraphs = paragraphs.slice(charIndex);
          const lastSection = translatedSections[translatedSections.length - 1];
          lastSection.content += '\n\n' + remainingParagraphs.join('\n\n');
        }
        
        console.log(`⚠️ Fallback: Extracted ${translatedSections.length} sections from ${paragraphs.length} paragraphs using character-ratio distribution`);
      }

      // If still no sections, create one section with all translated content (better than falling back to English!)
      const finalSections = translatedSections.length > 0 ? translatedSections : [{
        type: 'custom',
        content: translatedContent
          .replace(/Title:\s*.*?(?:\n|$)/i, '')
          .replace(/Scripture:\s*.*?(?:\n|$)/i, '')
          .trim()
      }];

      const newTranslation: TranslatedSermon = {
        id: Date.now().toString(),
        originalId: selectedSermon.id,
        title: translatedTitle,
        content: translatedContent,
        language: selectedLanguage.name,
        timestamp: Date.now(),
        originalSermon: selectedSermon,
        sections: finalSections,
        translatedScripture: translatedScripture
      };

      setTranslatedSermons(prev => [newTranslation, ...prev]);
      setCurrentTranslation(newTranslation);

    } catch (error) {
      console.error('Translation error:', error);
      
      // More detailed error handling
      let errorMessage = 'Translation failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        // Try to extract meaningful error info from response
        if ('message' in error) {
          errorMessage = error.message as string;
        } else if ('error' in error) {
          errorMessage = error.error as string;
        } else {
          errorMessage = 'Translation failed - please try again';
        }
      }
      
      setTranslationError(errorMessage);
    } finally {
      setTranslating(false);
    }
  };

  const saveAsNewSermon = async (translation: TranslatedSermon) => {
    try {
      setSaving(true);
      setTranslationError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const originalSermon = translation.originalSermon || sermons.find(s => s.id === translation.originalId);
      if (!originalSermon) throw new Error('Original sermon not found');

      // Check if "Translated Sermons" series exists, create if not
      let translatedSeriesId = null;
      const { data: existingSeries, error: seriesSearchError } = await supabase
        .from('series')
        .select('id')
        .eq('user_id', user.id)
        .eq('title', 'Translated Sermons')
        .single();

      if (seriesSearchError && seriesSearchError.code !== 'PGRST116') {
        // Error other than "not found"
        throw seriesSearchError;
      }

      if (existingSeries) {
        translatedSeriesId = existingSeries.id;
      } else {
        // Create the "Translated Sermons" series
        const { data: newSeries, error: seriesCreateError } = await supabase
          .from('series')
          .insert({
            user_id: user.id,
            title: 'Translated Sermons',
            description: 'Sermons translated from English to other languages'
          })
          .select('id')
          .single();

        if (seriesCreateError) throw seriesCreateError;
        translatedSeriesId = newSeries.id;
      }
      
      // Get color mappings for section types
      const sectionStyles: Record<string, { bg: string; text: string; border: string }> = {
        'scripture': { bg: 'crimson', text: 'crimson', border: 'crimson' },
        'illustration': { bg: 'emerald', text: 'emerald', border: 'emerald' },
        'quote': { bg: 'electric', text: 'electric', border: 'electric' },
        'application': { bg: 'gold', text: 'gold', border: 'gold' },
        'main-point': { bg: 'fiery', text: 'fiery', border: 'fiery' },
        'custom': { bg: 'slate', text: 'slate', border: 'slate' }
      };

      // Reconstruct the sermon with proper section formatting
      let formattedContent = '';
      
      if (translation.sections && translation.sections.length > 0) {
        // Use the translated sections directly (they already have translated content)
        translation.sections.forEach((section, index) => {
          const translatedText = section.content; // Already translated!

          // Clean any leftover bracket markers
          const cleanedText = translatedText
            .replace(/\[PUNTO[_\s]NUMERO:\d+\]/gi, '')
            .replace(/\[POINT[_\s]NUMBER:\d+\]/gi, '')
            .replace(/\[ATTRIBUTION:.*?\]/gi, '')
            .replace(/\[CUSTOM[_\s]TITLE:.*?\]/gi, '')
            .trim();

          if (section.type === 'prose') {
            // Non-section prose — emit as raw HTML, no sermon-section wrapper
            if (cleanedText.includes('<')) {
              formattedContent += cleanedText + '\n';
            } else {
              cleanedText.split(/\n\n+/).forEach(para => {
                if (para.trim()) formattedContent += `<p>${para.trim()}</p>\n`;
              });
            }
            return;
          }

          // Regular sermon section
          const sectionId = crypto.randomUUID();
          let sectionHtml = `<div class="sermon-section sermon-section-${section.type}" data-type="${section.type}" data-section-id="${sectionId}"`;

          if (section.pointNumber) sectionHtml += ` data-point-number="${section.pointNumber}"`;
          if (section.attribution) sectionHtml += ` data-attribution="${section.attribution}"`;
          if (section.isSlide) sectionHtml += ` data-is-slide="true"`;
          if (section.customTitle) sectionHtml += ` data-custom-title="${section.customTitle}"`;
          sectionHtml += `>\n`;

          // Use HTML directly if <p> tags are present, otherwise split plain text
          if (cleanedText.includes('<p>')) {
            sectionHtml += cleanedText + '\n';
          } else {
            cleanedText.split(/\n\n+/).forEach(para => {
              if (para.trim()) sectionHtml += `<p>${para.trim()}</p>\n`;
            });
          }

          sectionHtml += '</div>\n\n';
          formattedContent += sectionHtml;
        });
      } else {
        // Fallback: simple paragraph formatting if no sections found
        const contentLines = translation.content.split('\n')
          .filter(line => {
            const trimmed = line.trim();
            return trimmed && 
                   !trimmed.startsWith('Title:') && 
                   !trimmed.startsWith('Scripture:') &&
                   !trimmed.match(/\[SECTION_/);
          });
        
        contentLines.forEach(line => {
          if (line.trim()) {
            formattedContent += `<p>${line.trim()}</p>\n`;
          }
        });
      }

      // Wrap in container with header
      const finalContent = `
        <div class="sermon-header">
          <h1>${translation.title}</h1>
          <p><strong>Scripture:</strong> ${translation.translatedScripture || originalSermon.scripture || 'Multiple Passages'}</p>
          <p><em>Translated from English to ${translation.language}</em></p>
        </div>
        <div class="sermon-content">
          ${formattedContent}
        </div>
      `.trim();

      const { error } = await supabase
        .from('sermons')
        .insert({
          user_id: user.id,
          title: translation.title,
          scripture: translation.translatedScripture || originalSermon.scripture,
          content: finalContent,
          status: 'draft',
          series_id: translatedSeriesId
        });

      if (error) throw error;

      alert('Translation saved as new sermon successfully!');

    } catch (error) {
      console.error('Save error:', error);
      setTranslationError(error instanceof Error ? error.message : 'Failed to save sermon');
    } finally {
      setSaving(false);
    }
  };

  const downloadWord = async (translation: TranslatedSermon) => {
    try {
      const originalSermon = translation.originalSermon || sermons.find(s => s.id === translation.originalId);
      
      // Import the docx library
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
      
      const children = [];

      // Add title
      children.push(
        new Paragraph({
          text: translation.title,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
        })
      );

      // Add scripture reference (use translated version if available)
      const scriptureText = translation.translatedScripture || originalSermon?.scripture;
      if (scriptureText) {
        children.push(
          new Paragraph({
            text: scriptureText,
            alignment: AlignmentType.CENTER,
            spacing: { after: 480 }
          })
        );
      }

      // Add translation note
      children.push(
        new Paragraph({
          text: `Translated from English to ${translation.language}`,
          alignment: AlignmentType.CENTER,
          spacing: { after: 480 },
          run: { italics: true }
        })
      );

      // If we have section metadata, use it for structured output
      if (translation.sections && translation.sections.length > 0) {
        // Add each section with its metadata (sections already contain translated content)
        translation.sections.forEach((section, index) => {
          const translatedText = section.content; // Already translated!
          const sectionTitle = section.customTitle || section.type.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          // Add section heading
          const fullTitle = section.pointNumber 
            ? `${sectionTitle} ${section.pointNumber}`
            : sectionTitle;
          
          children.push(
            new Paragraph({
              text: fullTitle,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 480, after: 240 }
            })
          );
          
          // Add section content
          const paragraphs = translatedText.split(/\n\n+/);
          paragraphs.forEach(para => {
            if (para.trim()) {
              children.push(
                new Paragraph({
                  text: para.trim(),
                  spacing: { after: 240 }
                })
              );
            }
          });
          
          // Add attribution for quotes
          if (section.attribution) {
            children.push(
              new Paragraph({
                text: `— ${section.attribution}`,
                alignment: AlignmentType.RIGHT,
                run: { italics: true },
                spacing: { after: 240 }
              })
            );
          }
        });
      } else {
        // Fallback: Process the translated content with better paragraph handling
        const lines = translation.content.split('\n').filter(line => {
          const trimmed = line.trim();
          return trimmed && 
                 !trimmed.startsWith('Title:') && 
                 !trimmed.startsWith('Título:') && 
                 !trimmed.startsWith('Scripture:') && 
                 !trimmed.startsWith('Escritura:');
        });

      let currentContent = '';
        
        for (let i = 0; i < lines.length; i++) {
          const trimmed = lines[i].trim();
          if (!trimmed) continue;

          // Check if it's a heading
          const isHeading = (
            (trimmed.endsWith(':') && trimmed.length < 80 && !trimmed.includes('.')) ||
            (trimmed === trimmed.toUpperCase() && trimmed.length > 5 && trimmed.length < 60 && !trimmed.includes('.')) ||
            /^[IVX]+\.\s/.test(trimmed) ||
            /^\d+\.\s/.test(trimmed)
          );

          if (isHeading) {
            // Add any accumulated content as paragraphs first
            if (currentContent) {
              const paragraphs = currentContent.split(/(?<=[.!?])\s+(?=[A-Z])/);
              paragraphs.forEach(para => {
                if (para.trim().length > 0) {
                  children.push(
                    new Paragraph({
                      text: para.trim(),
                      spacing: { after: 240 }
                    })
                  );
                }
              });
              currentContent = '';
            }
            
            // Add heading
            children.push(
              new Paragraph({
                text: trimmed.replace(/:$/, ''),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 480, after: 240 }
              })
            );
          } else {
            // Accumulate content for proper paragraph processing
            if (currentContent) {
              currentContent += ' ' + trimmed;
            } else {
              currentContent = trimmed;
            }
            
            // Check if we should break into a paragraph (end of sentence or long content)
            if (trimmed.match(/[.!?]\s*$/) || currentContent.length > 500) {
              const paragraphs = currentContent.split(/(?<=[.!?])\s+(?=[A-Z])/);
              
              if (paragraphs.length > 1) {
                // Add first paragraph(s) and keep the rest
                for (let j = 0; j < paragraphs.length - 1; j++) {
                  if (paragraphs[j].trim().length > 0) {
                    children.push(
                      new Paragraph({
                        text: paragraphs[j].trim(),
                        spacing: { after: 240 }
                      })
                    );
                  }
                }
                currentContent = paragraphs[paragraphs.length - 1];
              } else if (currentContent.length > 500) {
                // Force break for very long content
                children.push(
                  new Paragraph({
                    text: currentContent.trim(),
                    spacing: { after: 240 }
                  })
                );
                currentContent = '';
              }
            }
          }
        }
        
        // Add any remaining content
        if (currentContent) {
          const paragraphs = currentContent.split(/(?<=[.!?])\s+(?=[A-Z])/);
          paragraphs.forEach(para => {
            if (para.trim().length > 0) {
              children.push(
                new Paragraph({
                  text: para.trim(),
                  spacing: { after: 240 }
                })
              );
            }
          });
        }
      }

      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440, right: 1440, bottom: 1440, left: 1440
              }
            }
          },
          children
        }]
      });

      const blob = await Packer.toBlob(doc);
      
      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${translation.title.toLowerCase().replace(/\s+/g, '-')}-${translation.language.toLowerCase()}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Word document generation error:', error);
      alert('Failed to generate Word document. Please try again.');
    }
  };

  const downloadPDF = (translation: TranslatedSermon) => {
    try {
      const originalSermon = translation.originalSermon || sermons.find(s => s.id === translation.originalId);
      
      const doc = new jsPDF();
      let yPos = createStyledPDF({
        title: translation.title,
        subtitle: `Translated to ${translation.language}`,
        date: originalSermon?.date ? parseISO(originalSermon.date).toLocaleDateString() : new Date().toLocaleDateString()
      }, doc);

      // Use translated scripture if available
      const scriptureText = translation.translatedScripture || originalSermon?.scripture;
      if (scriptureText) {
        yPos = addSection(doc, 'Scripture', scriptureText, yPos);
      }

      // If we have section metadata, use it for structured output
      if (translation.sections && translation.sections.length > 0) {
        // Add each section with its metadata (sections already contain translated content)
        translation.sections.forEach((section, index) => {
          const translatedText = section.content; // Already translated!
          const sectionTitle = section.customTitle || section.type.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          // Add point number if present
          const fullTitle = section.pointNumber 
            ? `${sectionTitle} ${section.pointNumber}`
            : sectionTitle;
          
          yPos = addSection(doc, fullTitle, translatedText, yPos);
          
          // Add attribution for quotes
          if (section.attribution) {
            yPos = addSection(doc, '', `— ${section.attribution}`, yPos);
          }
        });
        
        doc.save(`${translation.title.toLowerCase().replace(/\s+/g, '-')}-${translation.language.toLowerCase()}.pdf`);
        return;
      }

      // Fallback: Parse and format the translated content with better structure
      const lines = translation.content.split('\n').filter(line => line.trim());
      let currentSection = '';
      let currentParagraphs: string[] = [];
      
      // Track content for better processing
      let hasProcessedContent = false;
      
      for (const line of lines) {
        const trimmed = line.trim();
        
        // Skip title and scripture lines
        if (trimmed.startsWith('Title:') || trimmed.startsWith('Título:') || 
            trimmed.startsWith('Scripture:') || trimmed.startsWith('Escritura:')) {
          continue;
        }
        
        // More comprehensive section detection
        const isSection = (
          trimmed.length > 0 && trimmed.length < 80 && 
          (
            trimmed === trimmed.toUpperCase() || 
            /^[IVX]+\./.test(trimmed) || 
            /^\d+\./.test(trimmed) ||
            /^[A-Z][^.]*:$/.test(trimmed) ||
            trimmed.includes('Introducción') || trimmed.includes('Introduction') ||
            trimmed.includes('Conclusión') || trimmed.includes('Conclusion') ||
            trimmed.includes('Punto') || trimmed.includes('Point') ||
            trimmed.includes('Primero') || trimmed.includes('First') ||
            trimmed.includes('Segundo') || trimmed.includes('Second') ||
            trimmed.includes('Tercero') || trimmed.includes('Third') ||
            trimmed.includes('Aplicación') || trimmed.includes('Application')
          )
        );
        
        if (isSection) {
          // Add previous section if it exists
          if (currentSection && currentParagraphs.length > 0) {
            yPos = addSection(doc, currentSection, currentParagraphs, yPos);
            currentParagraphs = [];
            hasProcessedContent = true;
          }
          
          currentSection = trimmed;
        } else if (trimmed.length > 0) {
          // Handle very long paragraphs by splitting them
          if (trimmed.length > 500) {
            // Split long paragraphs at sentence boundaries
            const sentences = trimmed.split(/(?<=[.!?])\s+/);
            let currentParagraph = '';
            
            for (const sentence of sentences) {
              if ((currentParagraph + sentence + ' ').length > 500) {
                if (currentParagraph) {
                  currentParagraphs.push(currentParagraph.trim());
                  currentParagraph = sentence + ' ';
                } else {
                  currentParagraphs.push(sentence);
                }
              } else {
                currentParagraph += sentence + ' ';
              }
            }
            
            if (currentParagraph) {
              currentParagraphs.push(currentParagraph.trim());
            }
          } else {
            currentParagraphs.push(trimmed);
          }
        }
      }
      
      // Add final section
      if (currentSection && currentParagraphs.length > 0) {
        yPos = addSection(doc, currentSection, currentParagraphs, yPos);
        hasProcessedContent = true;
      } else if (currentParagraphs.length > 0) {
        yPos = addSection(doc, 'Content', currentParagraphs, yPos);
        hasProcessedContent = true;
      }
      
      // If no structured content was found, add all content as one section
      if (!hasProcessedContent) {
        const allContent = lines.filter(line => {
          const trimmed = line.trim();
          return trimmed && 
                 !trimmed.startsWith('Title:') && 
                 !trimmed.startsWith('Título:') && 
                 !trimmed.startsWith('Scripture:') && 
                 !trimmed.startsWith('Escritura:');
        });
        
        if (allContent.length > 0) {
          yPos = addSection(doc, 'Sermon Content', allContent, yPos);
        }
      }

      // Add translation note
      yPos = addSection(doc, 'Translation Note', [
        `This sermon was translated from English to ${translation.language} using AI translation.`,
        'Please review for accuracy and cultural appropriateness before use.'
      ], yPos);

      doc.save(`${translation.title.toLowerCase().replace(/\s+/g, '-')}-${translation.language.toLowerCase()}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  

  const deleteTranslation = (id: string) => {
    setTranslatedSermons(prev => {
      const updated = prev.filter(t => t.id !== id);
      // Update localStorage
      if (updated.length === 0) {
        localStorage.removeItem('sermon_translations');
      } else {
        localStorage.setItem('sermon_translations', JSON.stringify(updated));
      }
      return updated;
    });
    if (currentTranslation?.id === id) {
      setCurrentTranslation(null);
    }
  };

  const clearAllTranslations = () => {
    if (window.confirm('Are you sure you want to delete all translations? This cannot be undone.')) {
      setTranslatedSermons([]);
      setCurrentTranslation(null);
      localStorage.removeItem('sermon_translations');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-electric/10 rounded-lg p-3">
            <Languages className="h-6 w-6 text-electric" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-midnight">Sermon Translator</h2>
            <p className="text-slate">Translate your sermons into different languages using AI</p>
          </div>
        </div>

        {translationError && (
          <div className="mb-6 p-4 bg-crimson/10 border border-crimson/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-crimson mt-0.5" />
            <div>
              <p className="text-crimson font-medium">Translation Error</p>
              <p className="text-crimson/80 text-sm">{translationError}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-midnight mb-2">
              Select Sermon to Translate
            </label>
            <select
              value={selectedSermonId}
              onChange={(e) => setSelectedSermonId(e.target.value)}
              className="w-full p-3 border border-sand/30 rounded-lg focus:ring-2 focus:ring-electric/20 focus:border-electric"
            >
              <option value="">Choose a sermon...</option>
              {sermons.map((sermon) => (
                <option key={sermon.id} value={sermon.id}>
                  {sermon.title} {sermon.scripture && `(${sermon.scripture})`}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-midnight mb-2">
              Target Language
            </label>
            <div className="relative">
              <input
                type="text"
                value={selectedLanguage ? `${selectedLanguage.name} (${selectedLanguage.nativeName})` : languageSearchTerm}
                onChange={(e) => {
                  setLanguageSearchTerm(e.target.value);
                  setShowLanguageDropdown(true);
                  if (!e.target.value) setSelectedLanguage(null);
                }}
                onFocus={() => setShowLanguageDropdown(true)}
                placeholder="Search languages..."
                className="w-full p-3 border border-sand/30 rounded-lg focus:ring-2 focus:ring-electric/20 focus:border-electric pr-10"
              />
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {showLanguageDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-sand/30 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredLanguages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      setSelectedLanguage(language);
                      setLanguageSearchTerm('');
                      setShowLanguageDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-electric/5 flex items-center justify-between"
                  >
                    <span>{language.name}</span>
                    <span className="text-slate text-sm">{language.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <button
            onClick={translateSermon}
            disabled={!selectedSermonId || !selectedLanguage || translating}
            className="px-6 py-3 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {translating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Translating...
              </>
            ) : (
              <>
                <Languages className="h-4 w-4" />
                Translate Sermon
              </>
            )}
          </button>
        </div>
      </div>

      {/* Translation Results */}
      {translatedSermons.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-midnight">Translation Results ({translatedSermons.length})</h3>
            <button
              onClick={clearAllTranslations}
              className="px-3 py-1.5 text-sm text-crimson hover:text-crimson/80 border border-crimson/20 rounded hover:bg-crimson/5 flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Clear All
            </button>
          </div>
          
          <div className="space-y-4">
            {translatedSermons.map((translation) => {
              const originalSermon = sermons.find(s => s.id === translation.originalId);
              
              return (
                <div key={translation.id} className="border border-sand/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-midnight">{translation.title}</h4>
                      <p className="text-sm text-slate">
                        Translated to {translation.language} • {new Date(translation.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => saveAsNewSermon(translation)}
                        disabled={saving}
                        className="px-3 py-1.5 bg-emerald text-white text-sm rounded hover:bg-emerald/90 disabled:opacity-50 flex items-center gap-1"
                      >
                        {saving ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Save className="h-3 w-3" />
                        )}
                        Save as Sermon
                      </button>
                      <button
                        onClick={() => downloadPDF(translation)}
                        className="px-3 py-1.5 bg-electric text-white text-sm rounded hover:bg-electric/90 flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" />
                        PDF
                      </button>
                      <button
                        onClick={() => downloadWord(translation)}
                        className="px-3 py-1.5 bg-slate text-white text-sm rounded hover:bg-slate/90 flex items-center gap-1"
                      >
                        <FileText className="h-3 w-3" />
                        Word
                      </button>
                      <button
                        onClick={() => deleteTranslation(translation.id)}
                        className="p-1.5 text-crimson hover:text-crimson/80"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
